import React, {
    useMemo,
    useState
} from 'react';

import Select from 'react-select';

import { FaTimes, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';

import { formatSalesValue } from '../../../utils/formatUtils';
import PageStatus from '../../PageStatus/PageStatus';

const ConversionFactorTable = ({
    baseUrl,
    selectedOutlet,
    token,
    loading,
    error,
    data,
    setData,
    selectStyles
}) => {
    const [deleteLoading, setDeleteLoading] = useState({});
    const [deleteModal, setDeleteModal] = useState({
        open: false,
        item: null
    });
    const [recipeModal, setRecipeModal] = useState({
        open: false,
        item: null
    });
    const [searchItem, setSearchItem] =
        useState(null);

    const productionItemOptions =
        useMemo(() => {

            const uniqueItems = [
                ...new Map(
                    data.map(item => [
                        item.production_item_name,
                        {
                            label:
                                item.production_item_name,
                            value:
                                item.production_item_name
                        }
                    ])
                ).values()
            ];

            return uniqueItems;

        }, [data]);

    const filteredData =
        useMemo(() => {

            if (!searchItem) {
                return data;
            }

            return data.filter(
                item =>
                    item.production_item_name ===
                    searchItem.value
            );

        }, [data, searchItem]);

    const handleDelete = async (id) => {
        try {

            setDeleteLoading(prev => ({
                ...prev,
                [id]: true
            }));

            await axios.delete(
                `${baseUrl}/conversionfactor/${id}`,
                {
                    data: {
                        token
                    }
                }
            );

            toast.success(
                "Conversion factor deleted successfully"
            );

            setData(prev =>
                prev.filter(
                    item => item.id !== id
                )
            );

        } catch (err) {

            console.error(err);

            toast.error(
                err.response?.data?.message ||
                "Failed to delete conversion factor"
            );

        } finally {

            setDeleteLoading(prev => ({
                ...prev,
                [id]: false
            }));
        }
    };

    return (
        <>
            <PageStatus
                loading={loading}
                error={error}
                loadingText="Loading conversion factors..."
            />

            {!loading && !error && (
                <>
                    <div className="mb-2 w-full md:w-96">

                        <Select
                            options={
                                productionItemOptions
                            }
                            value={searchItem}
                            onChange={setSearchItem}
                            placeholder="Search production item..."
                            isSearchable
                            isClearable
                            styles={selectStyles}
                        />

                    </div>

                    <div className="w-full overflow-x-auto overflow-y-auto border border-gray-200 rounded-xl max-h-[480px]">
                        <table className="min-w-full text-sm text-center">
                            <thead className="bg-gray-100 text-gray-700 top-0 sticky z-20">
                                <tr>
                                    <th className="p-2 py-3 font-semibold whitespace-nowrap">
                                        Production Item
                                    </th>

                                    <th className="p-2 py-3 font-semibold whitespace-nowrap">
                                        Production Recipe
                                    </th>

                                    <th className="p-2 py-3 font-semibold whitespace-nowrap">
                                        Recipe Cost
                                    </th>

                                    <th className="p-2 py-3 font-semibold whitespace-nowrap">
                                        Conversion Factor
                                    </th>

                                    <th className="p-2 py-3 font-semibold whitespace-nowrap">
                                        Item Cost
                                    </th>

                                    <th className="p-2 py-3 font-semibold whitespace-nowrap">
                                        Action
                                    </th>
                                </tr>
                            </thead>

                            <tbody className='divide-gray-200'>
                                {filteredData.length > 0 ? (
                                    filteredData.map((item) => {
                                        const recipeCost =
                                            Number(
                                                item.production_recipe_selling_price || 0
                                            );

                                        const factor =
                                            Number(
                                                item.factor || 0
                                            );

                                        const itemCost =
                                            factor > 0
                                                ? recipeCost / factor
                                                : 0;

                                        return (
                                            <tr
                                                key={item.id}
                                                className="border-t border-gray-100 bg-white hover:!bg-gray-50 cursor-pointer"
                                                onClick={() =>
                                                    setRecipeModal({
                                                        open: true,
                                                        item
                                                    })
                                                }
                                            >
                                                <td className="px-3 py-2.5">
                                                    {item.production_item_name}
                                                </td>

                                                <td className="px-3 py-2.5">
                                                    {item.production_recipe_name}
                                                </td>

                                                <td className="px-3 py-2.5">
                                                    {formatSalesValue(recipeCost)}
                                                </td>

                                                <td className="px-3 py-2.5">
                                                    {factor}
                                                </td>

                                                <td className="px-3 py-2.5 font-medium text-blue-700">
                                                    {formatSalesValue(itemCost)}
                                                </td>

                                                <td className="px-3 py-2.5">
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();

                                                            setDeleteModal({
                                                                open: true,
                                                                item
                                                            });
                                                        }}
                                                        disabled={
                                                            deleteLoading[item.id]
                                                        }
                                                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {deleteLoading[item.id]
                                                            ? "..."
                                                            : <FaTrash size={12} />
                                                        }
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            className="px-4 py-6 text-center text-gray-500"
                                        >
                                            No conversion factors found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {deleteModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-md rounded-xl bg-white shadow-xl p-4">
                        <h3 className="text-lg font-semibold text-gray-800">
                            Delete Conversion Factor
                        </h3>

                        <p className="text-sm text-gray-600 mt-2">
                            Are you sure you want to delete the conversion factor for{" "}

                            <span className="font-medium text-gray-800">
                                {deleteModal.item?.production_recipe_name}
                            </span>

                            ?
                        </p>

                        <div className="flex justify-end gap-2 pt-4">
                            <button
                                type="button"
                                onClick={() =>
                                    setDeleteModal({
                                        open: false,
                                        item: null
                                    })
                                }
                                disabled={
                                    deleteLoading[
                                    deleteModal.item?.id
                                    ]
                                }
                                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                onClick={async () => {
                                    await handleDelete(
                                        deleteModal.item.id
                                    );

                                    setDeleteModal({
                                        open: false,
                                        item: null
                                    });
                                }}
                                disabled={
                                    deleteLoading[
                                    deleteModal.item?.id
                                    ]
                                }
                                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
                            >
                                {deleteLoading[
                                    deleteModal.item?.id
                                ]
                                    ? "Deleting..."
                                    : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {recipeModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-5xl rounded-xl bg-white shadow-xl">
                        <div className="flex items-center justify-between border-b px-4 pt-3 pb-2">
                            <h1 className="text-lg font-semibold text-gray-800">
                                Recipe Details &nbsp; --- &nbsp;
                                <span className="text-sm text-gray-500">
                                    {recipeModal.item?.production_recipe_name}
                                </span>
                            </h1>

                            <button
                                onClick={() =>
                                    setRecipeModal({
                                        open: false,
                                        item: null
                                    })
                                }
                                className="flex items-center justify-center w-8 h-8 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition"
                            >
                                <FaTimes size={14} />
                            </button>
                        </div>

                        <div className="p-4 overflow-x-auto">
                            <div className="border rounded-lg overflow-hidden">
                                <table className="min-w-full text-sm text-center">
                                    <thead className="bg-gray-100 text-gray-700">
                                        <tr>
                                            <th className="px-3 py-3 font-semibold">
                                                Item Name
                                            </th>

                                            <th className="px-3 py-3 font-semibold">
                                                UOM
                                            </th>

                                            <th className="px-3 py-3 font-semibold">
                                                Quantity
                                            </th>

                                            <th className="px-3 py-3 font-semibold">
                                                Rate
                                            </th>

                                            <th className="px-3 py-3 font-semibold">
                                                Total Cost
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {recipeModal.item?.recipe_details?.items
                                            ?.length > 0 ? (
                                            recipeModal.item.recipe_details.items.map(
                                                (recipeItem) => (
                                                    <tr
                                                        key={recipeItem.id}
                                                        className="border-t border-gray-100 hover:bg-gray-50"
                                                    >
                                                        <td className="px-3 py-2.5">
                                                            {recipeItem.name}
                                                        </td>

                                                        <td className="px-3 py-2.5">
                                                            {recipeItem.uom}
                                                        </td>

                                                        <td className="px-3 py-2.5">
                                                            {recipeItem.quantity}
                                                        </td>

                                                        <td className="px-3 py-2.5">
                                                            {formatSalesValue(recipeItem.rate)}
                                                        </td>

                                                        <td className="px-3 py-2.5 font-medium text-blue-700">
                                                            {formatSalesValue(recipeItem.cost_per_unit)}
                                                        </td>
                                                    </tr>
                                                )
                                            )

                                        ) : (
                                            <tr>
                                                <td
                                                    colSpan={5}
                                                    className="px-4 py-6 text-center text-gray-500"
                                                >
                                                    No recipe items found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ConversionFactorTable;